import { useQuery } from '@apollo/client';
import { Autocomplete } from '@components/AutoComplete';
import { TextField } from '@components/TextField';
import { GET_AVAILABLE_HIGHER_UPS, GET_AVAILABLE_HIGHER_UPS_UPERCUT } from '@gql/organization-users';
import { Avatar, Box, CircularProgress, FormControl, FormHelperText, FormLabel as MuiFormLabel, Stack, Typography, styled } from '@mui/material';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useAuth } from '@contexts/AuthContext';
import { useProId } from '@contexts/ProIdContext';

interface HigherUpOption {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role?: string;
    color?: string;
    file?: { _id: string; url: string };
}

const FormLabel = styled(MuiFormLabel)(() => ({
    marginBottom: '4px',
}));

interface InputHigherUpProps {
    name: string;
    control: any;
    label?: string;
    disabled?: boolean;
    required?: boolean;
    proId: string;
}

const InputHigherUp = ({ name, control, label = 'Responsable hiérarchique', disabled, required, proId }: InputHigherUpProps) => {
    const { isUpercut } = useAuth();
    const { isProIdRequired } = useProId();
    const queryToUse = isUpercut && !isProIdRequired ? GET_AVAILABLE_HIGHER_UPS_UPERCUT : GET_AVAILABLE_HIGHER_UPS;
    const { data, loading } = useQuery(queryToUse, {
        variables: { proId }
    });

    const options: HigherUpOption[] = useMemo(() => {
        if (isUpercut && !isProIdRequired) return data?.getAvailableHigherUpsUpercut ?? [];
        return data?.getAvailableHigherUps ?? [];
    }, [data, isUpercut, isProIdRequired]);

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
                const selectedOption = options.find(opt => opt._id === value) ?? null;

                return (
                    <FormControl fullWidth error={!!error} sx={{ mb: 2 }}>
                        <FormLabel htmlFor={name}>{label}</FormLabel>
                        <Autocomplete
                            options={options}
                            value={selectedOption}
                            loading={loading}
                            disabled={disabled}
                            onChange={(_, newValue: HigherUpOption | null) => {
                                onChange(newValue?._id ?? '');
                            }}
                            getOptionLabel={(option: HigherUpOption) =>
                                `${option.firstName} ${option.lastName}`
                            }
                            isOptionEqualToValue={(option: HigherUpOption, val: HigherUpOption) =>
                                option._id === val._id
                            }
                            filterOptions={(opts: HigherUpOption[], { inputValue }) => {
                                const lower = inputValue.toLowerCase();
                                return opts.filter(opt =>
                                    `${opt.firstName} ${opt.lastName}`.toLowerCase().includes(lower) ||
                                    opt.email.toLowerCase().includes(lower)
                                );
                            }}
                            renderOption={(props, option: HigherUpOption) => (
                                <li {...props} key={option._id}>
                                    <Stack direction="row" alignItems="center" gap={1.5}>
                                        <Avatar
                                            src={option.file?.url}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                fontSize: 14,
                                                bgcolor: option.color ?? 'primary.light',
                                            }}
                                        >
                                            {option.firstName?.charAt(0)?.toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" fontWeight={500}>
                                                {option.firstName} {option.lastName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.email}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Rechercher par nom ou email..."
                                    error={!!error}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loading && <CircularProgress size={16} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        {selectedOption && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 0.5 }}>
                                {selectedOption.email}
                            </Typography>
                        )}
                        {error && <FormHelperText>{error.message}</FormHelperText>}
                    </FormControl>
                );
            }}
        />
    );
};

export default InputHigherUp;
